// apps/admin/src/hooks/use-multi-select.ts
//
// P5 UI Polish W2 T4 — multi-select hook (spec §3.2).
//
// Tracks a Set<zoneId>. Plain click replaces the selection;
// shift-click adds or removes from it. clear() empties the set.
//
// W6 T5 addition: selectMany(ids, additive) — bulk-select used by
// CanvasMarquee so it can replace or union the selection without
// calling select() in a loop (which would thrash state one-by-one).

import { useCallback, useState } from 'react';

export interface UseMultiSelectResult {
  selectedIds: ReadonlyArray<string>;
  select: (id: string, shiftKey: boolean) => void;
  /** Bulk-select. When additive=true, unions ids with current selection.
   *  When additive=false, replaces the selection with exactly ids. */
  selectMany: (ids: ReadonlyArray<string>, additive: boolean) => void;
  clear: () => void;
}

export function useMultiSelect(): UseMultiSelectResult {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const select = useCallback((id: string, shiftKey: boolean) => {
    setSelected((prev) => {
      if (!shiftKey) return new Set([id]);
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectMany = useCallback((ids: ReadonlyArray<string>, additive: boolean) => {
    setSelected((prev) => {
      if (!additive) return new Set(ids);
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  return { selectedIds: Array.from(selected), select, selectMany, clear };
}
